import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import {
  Calendar2Date,
  Cash,
  Check2Circle,
  EyeFill,
  EyeSlashFill,
  FileEarmarkTextFill,
  PersonCircle,
  Search,
  XCircle,
} from 'react-bootstrap-icons'
import { toast } from 'react-toastify'

import { CustomButton } from '../../../components/CustomButton'
import { CustomSpinner } from '../../../components/CustomSpinner'
import apinode from '../../../services/apinode'
import TrocasDevolucoesService from '../../../services/database/TrocasDevolucoesService'
import { formatDate } from '../../../utils/formatDate'
import { formatPrice } from '../../../utils/formatPrice'
import { sendErrorApiMessage } from '../../../utils/sendErrorApiMessage'

import './styles.css'

export function ModalDevolucao({ isOpen, onClose, loadRefunds }) {
  const [order, setOrder] = useState({})
  const [orderNumber, setOrderNumber] = useState('')
  const [itemsToRefund, setItemsToRefund] = useState([])
  const [isRequestLoading, setIsRequestLoading] = useState(false)
  const [isItemsVisible, setIsItemsVisible] = useState(false)
  const [isLoadingRefundRequest, setIsLoadingRefundRequest] = useState(false)
  const [willAddBalance, setWillAddBalance] = useState(false)

  const searchBtnRef = useRef(null)

  const totalSaldo = itemsToRefund.reduce((acc, item) => {
    const unit = item.valor_total_item / item.quantidade_nova
    return acc + unit * Number(item.quantity_to_refund)
  }, 0)

  function handleAddItem(item) {
    setItemsToRefund((prev) => [...prev, item])
  }

  function handleRemoveItem(item) {
    setItemsToRefund((prev) => prev.filter((i) => i.id !== item.id))
  }

  async function handleConfirm() {
    setIsLoadingRefundRequest(true)
    try {
      const itemsFormatted = itemsToRefund.map((i) => ({
        id_produto: i.id,
        sku_produto: i.codigo,
        quantidade: Number(i.quantity_to_refund),
        valorunitario: i.valor_total_item / i.quantidade_nova,
      }))

      await TrocasDevolucoesService.create({
        id_cliente: order.id_cliente,
        id_pedido: order.pedido,
        id_funcionario: localStorage.idusuario,
        items: itemsFormatted,
        willAddBalance,
      })

      toast.success('Devolução feita com sucesso!')
      onClose()
      loadRefunds()
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao realizar a devolução!')
    } finally {
      setIsLoadingRefundRequest(false)
    }
  }

  async function handleSearchOrder() {
    setIsRequestLoading(true)
    try {
      const response = await apinode.get(
        `/pedido/pedido-editar/${orderNumber}/${localStorage.idconta}`,
      )
      setOrder(response.data.dadospedido)
      setIsItemsVisible(false)
      setItemsToRefund([])
    } catch (error) {
      sendErrorApiMessage(error, `Pedido #${orderNumber} não encontrado.`)
    } finally {
      setIsRequestLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      setOrder({})
      setOrderNumber('')
      setItemsToRefund([])
      setIsRequestLoading(false)
      setIsItemsVisible(false)
      setWillAddBalance(false)
    }
  }, [isOpen])

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size="lg"
      centered
      dialogClassName="modal-devolucoes-dialog"
      contentClassName="modal-devolucoes-content"
    >
      {/* Header */}
      <Modal.Header closeButton style={{ borderBottom: '1px solid #e8e8e8', padding: '1rem 1.25rem' }}>
        <div>
          <Modal.Title style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>
            Devolução de mercadoria
          </Modal.Title>
          <p style={{ fontSize: 12, color: '#888', margin: 0 }}>
            Busque um pedido e selecione os itens a devolver
          </p>
        </div>
      </Modal.Header>

      {/* Body */}
      <Modal.Body style={{ padding: '1.25rem' }}>

        {/* Busca de pedido */}
        <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem' }}>
          <Form.Control
            type="text"
            placeholder="Número do pedido..."
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') searchBtnRef.current?.click() }}
            style={{ fontSize: 13 }}
          />
          <CustomButton
            ref={searchBtnRef}
            isLoading={isRequestLoading}
            isLoadingText="Buscando..."
            isDisabled={!orderNumber}
            onClick={handleSearchOrder}
          >
            <Search size={15} /> Buscar
          </CustomButton>
        </div>

        {/* Dados do pedido */}
        {order.cliente && (
          <div style={{ border: '1px solid #e8e8e8', borderRadius: 10, overflow: 'hidden', marginBottom: '1.25rem' }}>

            {/* Cabeçalho do pedido */}
            <div style={{ background: '#f7f7f8', padding: '10px 14px', borderBottom: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
                Pedido <span style={{ color: '#185FA5' }}>#{order.pedido}</span>
              </span>
              <button
                onClick={() => setIsItemsVisible((p) => !p)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 12, padding: '4px 12px', borderRadius: 6,
                  border: '1px solid #dee2e6', background: '#fff',
                  color: '#555', cursor: 'pointer',
                }}
              >
                {isItemsVisible ? <><EyeSlashFill size={13} /> Ocultar itens</> : <><EyeFill size={13} /> Ver itens</>}
              </button>
            </div>

            {/* Infos do pedido */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 0 }}>
              {[
                { icon: <PersonCircle size={14} />, label: 'Cliente', value: order.cliente },
                { icon: <FileEarmarkTextFill size={14} />, label: 'CPF', value: order.cpf },
                { icon: <Calendar2Date size={14} />, label: 'Data', value: formatDate(order.data, 'dd/MM/yyyy') },
                { icon: <Cash size={14} />, label: 'Valor', value: formatPrice(order.valor) },
              ].map(({ icon, label, value }) => (
                <div key={label} style={{ padding: '10px 14px', borderRight: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {icon} {label}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lista de itens */}
        {isItemsVisible && (
          <div>
            {!order.items || order.items.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#aaa', padding: '1.5rem 0', fontSize: 13 }}>
                Este pedido não possui itens.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
                  Digite a quantidade a devolver e marque o item para incluí-lo na devolução.
                </p>
                {order.items.map((item) => (
                  <Item
                    key={item.codigo}
                    item={item}
                    addItem={handleAddItem}
                    removeItem={handleRemoveItem}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer style={{ borderTop: '1px solid #e8e8e8', padding: '0.75rem 1.25rem', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Saldo em haver */}
          <label
            htmlFor="check-balance"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
              border: `1px solid ${willAddBalance ? '#3B6D11' : '#dee2e6'}`,
              background: willAddBalance ? '#EAF3DE' : '#fafafa',
              fontSize: 12, color: willAddBalance ? '#3B6D11' : '#555',
              fontWeight: willAddBalance ? 600 : 400,
              transition: 'all 0.15s',
              opacity: itemsToRefund.length === 0 ? 0.4 : 1,
            }}
          >
            <Form.Check
              id="check-balance"
              type="checkbox"
              disabled={itemsToRefund.length === 0}
              checked={willAddBalance}
              onChange={() => setWillAddBalance((p) => !p)}
              style={{ margin: 0 }}
            />
            Adicionar saldo em haver
          </label>

          {/* Resumo */}
          {itemsToRefund.length > 0 && (
            <span style={{ fontSize: 12, color: '#888' }}>
              {itemsToRefund.length} item{itemsToRefund.length > 1 ? 'ns' : ''} selecionado{itemsToRefund.length > 1 ? 's' : ''}
              {' · '}
              <strong style={{ color: '#3B6D11' }}>{formatPrice(totalSaldo)}</strong>
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onClose}
            style={{ padding: '7px 16px', fontSize: 13, borderRadius: 6, border: '1px solid #dee2e6', background: '#fff', color: '#555', cursor: 'pointer' }}
          >
            Cancelar
          </button>
          <CustomButton
            isDisabled={itemsToRefund.length === 0}
            isLoading={isLoadingRefundRequest}
            isLoadingText="Salvando..."
            onClick={handleConfirm}
            color="#185FA5"
          >
            <Check2Circle size={15} /> Confirmar devolução
          </CustomButton>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

// ---- Componente de Item ----

const Item = forwardRef(({ item, addItem, removeItem }, ref) => {
  const [quantityRefund, setQuantityRefund] = useState('')
  const [itemFromRefund, setItemFromRefund] = useState({})
  const [isChecked, setIsChecked] = useState(false)
  const [isLoadingRefund, setIsLoadingRefund] = useState(true)

  const unitPrice = Number(item.valor_total_item) / Number(item.quantidade_nova)
  const totalPrice = unitPrice * item.quantidade_nova
  const qtdJaDevolvida = Number(itemFromRefund.quantidade ?? 0)
  const qtdDisponivel = Number(item.quantidade_nova) - qtdJaDevolvida
  const isFullyRefunded = qtdDisponivel <= 0

  const checkIsDisabled =
    !quantityRefund ||
    Number(quantityRefund) <= 0 ||
    Number(quantityRefund) > qtdDisponivel

  const saldoAReceber = unitPrice * Number(quantityRefund || 0)

  const verifyItemRefund = useCallback(async () => {
    setIsLoadingRefund(true)
    try {
      const data = await TrocasDevolucoesService.getOne({
        id_produto: item.id,
        id_pedido: item.id_pedido,
      })
      if (data) setItemFromRefund(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível recuperar as informações!')
    } finally {
      setIsLoadingRefund(false)
    }
  }, [item.id, item.id_pedido])

  useEffect(() => { verifyItemRefund() }, [verifyItemRefund])

  if (isLoadingRefund) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', border: '1px solid #e8e8e8', borderRadius: 8, background: '#fafafa' }}>
        <CustomSpinner color="#006fff" />
        <span style={{ fontSize: 12, color: '#aaa' }}>Carregando item...</span>
      </div>
    )
  }

  return (
    <div
      ref={ref}
      style={{
        border: `1px solid ${isChecked ? '#185FA5' : isFullyRefunded ? '#e8e8e8' : '#e8e8e8'}`,
        borderRadius: 10,
        overflow: 'hidden',
        background: isFullyRefunded ? '#fafafa' : isChecked ? 'rgba(24,95,165,0.04)' : '#fff',
        opacity: isFullyRefunded ? 0.6 : 1,
        transition: 'all 0.15s',
        position: 'relative',
      }}
    >
      {/* Badge devolvido */}
      {isFullyRefunded && (
        <div style={{
          position: 'absolute', top: 10, right: 10,
          background: '#f1f1f1', color: '#888',
          fontSize: 10, fontWeight: 700, padding: '2px 8px',
          borderRadius: 20, letterSpacing: '0.05em',
        }}>
          DEVOLVIDO
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, padding: '12px 14px' }}>
        {/* Imagem */}
        <img
          src={`${process.env.REACT_APP_IMAGE_URL_API}/${item.img_capa || 'no-image.jpg'}`}
          alt={item.descricao}
          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #e8e8e8', flexShrink: 0 }}
          onError={(e) => { e.target.src = `${process.env.REACT_APP_IMAGE_URL_API}/no-image.jpg` }}
        />

        {/* Infos */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
            <div>
              <span style={{ fontSize: 11, color: '#aaa', fontFamily: 'monospace' }}>#{item.codigo}</span>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#222', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.descricao}
              </p>
            </div>
          </div>

          {/* Estatísticas do item */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
            {[
              { label: 'Qtd. pedido', value: item.quantidade_nova },
              { label: 'Vl. unitário', value: formatPrice(unitPrice) },
              { label: 'Vl. total', value: formatPrice(totalPrice) },
              { label: 'Já devolvido', value: qtdJaDevolvida, color: qtdJaDevolvida > 0 ? '#A32D2D' : '#aaa' },
              { label: 'Disponível', value: qtdDisponivel, color: qtdDisponivel > 0 ? '#3B6D11' : '#A32D2D' },
            ].map(({ label, value, color }) => (
              <div key={label}>
                <div style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: color ?? '#333' }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Controles */}
          {!isFullyRefunded && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>Qtd. a devolver:</label>
                <Form.Control
                  type="number"
                  min={1}
                  max={qtdDisponivel}
                  value={quantityRefund}
                  onChange={(e) => {
                    setIsChecked(false)
                    removeItem(item)
                    setQuantityRefund(e.target.value)
                  }}
                  style={{ width: 80, height: 32, fontSize: 13 }}
                />
              </div>

              {quantityRefund && Number(quantityRefund) > 0 && (
                <span style={{ fontSize: 12, color: '#3B6D11', fontWeight: 600 }}>
                  Saldo: {formatPrice(saldoAReceber)}
                </span>
              )}

              <label
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '5px 12px', borderRadius: 6, cursor: checkIsDisabled ? 'not-allowed' : 'pointer',
                  border: `1px solid ${isChecked ? '#185FA5' : '#dee2e6'}`,
                  background: isChecked ? '#185FA5' : '#fafafa',
                  fontSize: 12, color: isChecked ? '#fff' : '#555',
                  fontWeight: 600, transition: 'all 0.15s',
                  opacity: checkIsDisabled ? 0.4 : 1,
                  marginLeft: 'auto',
                }}
              >
                <Form.Check
                  type="checkbox"
                  checked={isChecked}
                  disabled={checkIsDisabled}
                  onChange={() => {
                    const next = !isChecked
                    setIsChecked(next)
                    if (next) addItem({ ...item, quantity_to_refund: quantityRefund })
                    else removeItem(item)
                  }}
                  style={{ margin: 0 }}
                />
                {isChecked ? <><Check2Circle size={13} /> Selecionado</> : 'Selecionar'}
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})